import { createRouter, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { reportFrontendError, type FrontendErrorReport } from "./lib/global-error-handlers";
import { classifyFrontendError, isRouteAssetLoadError } from "./lib/frontend-error-diagnostics";
import { routeTree } from "./routeTree.gen";
import { PRODUCT_HOME } from "./lib/product";

const ROUTE_ASSET_RELOAD_KEY = "genrl-route-asset-reload-at";

function DefaultErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  const routeAssetLoadError = isRouteAssetLoadError(error);
  const [report, setReport] = useState<FrontendErrorReport | null>(null);
  const diagnostic = useMemo(
    () => classifyFrontendError(error, {
      source: "router_boundary",
      extra: { route_asset_load_error: routeAssetLoadError },
    }),
    [error, routeAssetLoadError],
  );

  useEffect(() => {
    const nextReport = reportFrontendError({
      source: "router_boundary",
      error,
      extra: {
        route_asset_load_error: routeAssetLoadError,
      },
    });
    setReport(nextReport);
  }, [error, routeAssetLoadError]);

  useEffect(() => {
    if (!routeAssetLoadError || typeof window === "undefined") return;
    try {
      const lastReloadAt = Number(window.sessionStorage.getItem(ROUTE_ASSET_RELOAD_KEY) || 0);
      if (Date.now() - lastReloadAt < 15000) return;
      window.sessionStorage.setItem(ROUTE_ASSET_RELOAD_KEY, String(Date.now()));
    } catch {
      // If sessionStorage is unavailable, a single hard reload is still the safest recovery.
    }
    window.location.reload();
  }, [routeAssetLoadError]);

  return (
    <div className="genrl-route-canvas flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-lg rounded-[28px] border border-[var(--genrl-panel-border)] bg-[var(--genrl-panel-bg)] p-8 text-center shadow-[var(--genrl-panel-shadow)] backdrop-blur-2xl">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8 text-destructive"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
            />
          </svg>
        </div>
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--genrl-panel-ink-muted)]">
          {diagnostic.code}
        </p>
        <h1 className="mt-3 text-2xl font-bold tracking-tight text-[var(--genrl-panel-ink)]">
          {diagnostic.title}
        </h1>
        <p className="mt-2 text-sm leading-6 text-[var(--genrl-panel-ink-soft)]">
          {diagnostic.userMessage}
        </p>
        <div className="mt-5 rounded-xl border border-[var(--genrl-panel-border)] bg-[var(--genrl-panel-bg-soft)] p-3 text-left text-xs leading-5 text-[var(--genrl-panel-ink-soft)]">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold text-[var(--genrl-panel-ink)]">Error ID</span>
            <span className="font-mono">{report?.id || "logging..."}</span>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold text-[var(--genrl-panel-ink)]">Route</span>
            <span className="font-mono">
              {report?.path || (typeof window === "undefined" ? "" : window.location.pathname)}
            </span>
          </div>
          <p className="mt-3 break-words font-mono text-[11px] text-destructive">
            {diagnostic.technicalMessage}
          </p>
        </div>
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            onClick={() => {
              if (routeAssetLoadError && typeof window !== "undefined") {
                window.location.reload();
                return;
              }
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
            style={{ background: "var(--genrl-panel-accent)" }}
          >
            Try again
          </button>
          <a
            href={`/ui${PRODUCT_HOME}`}
            className="inline-flex items-center justify-center rounded-md border border-[var(--genrl-panel-border)] bg-[var(--genrl-panel-bg-soft)] px-4 py-2 text-sm font-medium text-[var(--genrl-panel-ink)] transition-opacity hover:opacity-80"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const getRouter = () => {
  const basepath = typeof window !== "undefined" && window.location.pathname.startsWith("/ui") ? "/ui" : "/";
  const router = createRouter({
    routeTree,
    basepath,
    context: {},
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
    defaultErrorComponent: DefaultErrorComponent,
  });

  return router;
};
